import { consoleRoutes, WALLBOARD_POLL_S } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { AppLink, Button, Card, CardHeader, Chip, EmptyState, ErrorState, Icon, Skeleton, buttonClasses, toast } from "@sagot/ui";
import { useEffect, useState } from "react";
import { usePermissions } from "../../../session/permissions";
import { useWorkspaceContext } from "../../../session/workspace-context";
import { isLiveChatOff, useLiveChatState } from "../inbox/use-presence";
import { PageHeader } from "../shared/page-header";
import { AgentBoard, AgentBoardSkeleton } from "./agent-board";
import type { ReassignChat } from "./reassign-menu";
import { reassignFailure, useReassign, useWallboard, type WallboardLink } from "./use-wallboard";
import { WaitingList, WaitingSkeleton } from "./waiting-list";
import { WallboardTiles, TilesSkeleton } from "./wallboard-tiles";
import { reassignTargets, secondsAgo, type ReassignTarget } from "./wallboard-model";

/** A clock that ticks every second while `active`, so "updated 2 s ago" stays true between snapshots. */
function useNow(active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    const timer = setInterval(() => { setNow(Date.now()); }, 1000);
    return () => { clearInterval(timer); };
  }, [active]);
  return now;
}

interface LiveBadgeProps {
  link: WallboardLink;
  /** When the figures were last replaced (the browser's clock, epoch ms). */
  updatedAt: number;
  now: number;
}

/** Whether the board is streaming or polling, and how old the figures are. The screen reader hears only the state, not the counting seconds. */
function LiveBadge({ link, updatedAt, now }: LiveBadgeProps) {
  const { t } = useTranslation();
  const seconds = secondsAgo(updatedAt, now);
  const polling = t("wallboard.board.polling", { seconds: WALLBOARD_POLL_S });
  const label =
    link === "live"
      ? t("wallboard.board.liveUpdated", { seconds })
      : link === "connecting"
        ? t("wallboard.board.connecting")
        : t("wallboard.board.reconnecting", { seconds: WALLBOARD_POLL_S });
  const spoken = link === "live" ? t("wallboard.board.live") : link === "connecting" ? t("wallboard.board.connecting") : polling;
  return (
    <Chip tone={link === "live" ? "success" : "warning"} size="lg" icon={link === "live" ? "activity" : "refresh-cw"} data-testid="wallboard-link" data-link={link}>
      <span className="sr-only" role="status">{spoken}</span>
      <span aria-hidden="true">{label}</span>
    </Chip>
  );
}

function PageSkeleton() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6" data-testid="wallboard-loading" aria-busy="true" aria-label={t("wallboard.board.loading")}>
      <TilesSkeleton />
      <div className="grid grid-cols-[minmax(0,1fr)] gap-4 2xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <Card>
          <CardHeader><Skeleton className="h-4 w-32" /></CardHeader>
          <AgentBoardSkeleton />
        </Card>
        <Card>
          <CardHeader><Skeleton className="h-4 w-28" /></CardHeader>
          <WaitingSkeleton />
        </Card>
      </div>
    </div>
  );
}

/** `/w/:ws/wallboard`: the supervisor's live view of the queue and the agents (RPT-003). */
export function WallboardPage() {
  const { t } = useTranslation();
  const { summary } = useWorkspaceContext();
  const permissions = usePermissions();
  const canAssign = permissions.can("inbox.assign") && !permissions.readOnly;
  const state = useLiveChatState(permissions.can("wallboard.view"));
  const on = state.data?.enabled === true;
  const board = useWallboard(on);
  const reassign = useReassign();
  const now = useNow(on);
  const snapshot = board.snapshot;
  const off = state.data?.enabled === false || isLiveChatOff(board.error);

  const onReassign = (chat: ReassignChat, target: ReassignTarget) => {
    reassign.mutate(
      { conversationId: chat.id, toMemberId: target.memberId },
      {
        onSuccess: (done) => {
          toast.success(t(done.changed ? "wallboard.board.reassign.done" : "wallboard.board.reassign.alreadyThere", { name: target.name }));
        },
        onError: (error) => {
          const failure = reassignFailure(error);
          toast.error(failure === "limit" ? t("wallboard.board.reassign.full", { name: target.name }) : failure === "gone" ? t("wallboard.board.reassign.gone") : t("wallboard.board.reassign.failed"));
        },
      },
    );
  };

  let body;
  if (off) {
    body = (
      <EmptyState
        icon="activity"
        title={t("wallboard.board.offTitle")}
        description={t("wallboard.board.offBody")}
        action={<AppLink href={consoleRoutes.inbox(summary.slug)} className={buttonClasses({ variant: "secondary" })}>{t("wallboard.board.offAction")}</AppLink>}
      />
    );
  } else if (state.isError || (on && board.isError && !snapshot)) {
    body = (
      <ErrorState
        title={t("wallboard.board.errorTitle")}
        description={t("wallboard.board.errorBody")}
        retryLabel={t("wallboard.board.retry")}
        onRetry={() => { if (state.isError) void state.refetch(); else board.refetch(); }}
      />
    );
  } else if (state.isPending || !on || !snapshot) {
    body = <PageSkeleton />;
  } else {
    const targets = reassignTargets(snapshot.agents, null);
    body = (
      <div className="space-y-6" data-testid="wallboard-board">
        {board.isError ? (
          <div role="alert" className="flex items-center justify-between gap-3 rounded-card border border-warning bg-warning-soft px-4 py-3 text-sm text-fg" data-testid="wallboard-stale">
            <span className="flex items-center gap-2"><Icon name="wifi-off" />{t("wallboard.board.stale", { seconds: secondsAgo(board.updatedAt, now) })}</span>
            <Button variant="secondary" size="sm" onClick={board.refetch}>{t("wallboard.board.retry")}</Button>
          </div>
        ) : null}
        <WallboardTiles tiles={snapshot.tiles} />
        <div className="grid grid-cols-[minmax(0,1fr)] gap-4 2xl:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
          <AgentBoard agents={snapshot.agents} canAssign={canAssign} pending={reassign.isPending} onReassign={onReassign} teamsHref={consoleRoutes.team(summary.slug)} />
          <WaitingList waiting={snapshot.waiting} total={snapshot.tiles.queue.total} targets={targets} canAssign={canAssign} pending={reassign.isPending} onAssign={onReassign} />
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl space-y-6" data-testid="wallboard-page">
      <PageHeader
        title={t("wallboard.board.title")}
        subtitle={t("wallboard.board.subtitle")}
        actions={on && snapshot ? <LiveBadge link={board.link} updatedAt={board.updatedAt} now={now} /> : null}
      />
      {body}
    </div>
  );
}
